import React, { useMemo, useEffect, useState } from "react";
import moment from "moment";
import get from "lodash.get";
import styled from "@emotion/styled";
import { connect } from "react-redux";
import { useIntl } from "react-intl";

import ContractUtils from "services/contract/utils";
import FlowActioner from "services/flow/actioner";
import FlowUtils from "services/flow/utils";

import ParkkiTheme from "themes/ParkkiTheme";

import FlowStatsSection from "../FlowStatsSection";
import FlowFilters from "../FlowFilters";

const Container = styled.div`
  display: flex;
  flex: 1;
  background-color: ${ParkkiTheme.colors.lightGrey};
  flex-direction: column;
`;

function FlowLayout({
  contract,
  currentFloor,
  flowMap,
  filters,
  endDate,
  startDate,
  selectedLayer,
  setFilter,
  retrieveAllFlowStats,
  retrieveAttendanceGraph,
  retrieveTravelTimeGraph,
  setSelectedLayers,
  travelTimeFilters,
  attendanceFilters,
  chronologyFilters,
  categoryCountFilters,
  retrieveCategoryCountGraph,
  retrieveChronologyGraph,
  retrieveTrafficChart,
  trafficChartFilters,
}) {
  const intl = useIntl();

  const [dates, setDates] = useState({ from: startDate, to: endDate });

  const isMultipleDays = useMemo(() => {
    return moment(endDate).diff(moment(startDate), "days") > 0;
  }, [startDate, endDate]);

  const formattedFlowTypeFilters = useMemo(() => {
    return filters?.availableFlowTypes?.map((value) => ({
      value,
      text:
        intl.messages[`flowScene.filters.flowTypes.${value?.toLowerCase()}`] ||
        value,
      selected: filters?.flowTypes?.indexOf(value) >= 0,
    }));
  }, [filters, intl]);

  useEffect(() => {
    let newSelectedLayer;

    if (selectedLayer?.floor_level === currentFloor) {
      newSelectedLayer = selectedLayer;
    } else {
      newSelectedLayer = FlowUtils.getDefaultSelectedLayer(
        flowMap,
        currentFloor
      );
    }

    if (newSelectedLayer) {
      setSelectedLayers([newSelectedLayer]);
    }
  }, [currentFloor]);

  useEffect(() => {
    async function initializeData() {
      if (flowMap?.initialized) {
        retrieveAllFlowStats();
      }
    }

    initializeData();
  }, [flowMap?.initialized, selectedLayer]);

  useEffect(() => {
    if (flowMap?.initialized) {
      const xAxis = attendanceFilters?.x_axis || "DAY";
      const filters = {
        x_axis: xAxis,
      };
      if (xAxis === "HOUR" && !isMultipleDays) {
        filters.group_by_hours = true;
      }

      retrieveAttendanceGraph(selectedLayer, filters);
    }
  }, [
    flowMap?.initialized,
    selectedLayer,
    attendanceFilters,
    retrieveAttendanceGraph,
    isMultipleDays,
  ]);

  useEffect(() => {
    if (flowMap?.initialized) {
      const xAxis = chronologyFilters?.x_axis || "24H";
      const filters = {};
      if (xAxis === "24H") {
        filters.x_axis = "HOUR";
        filters.group_by_hours = true;
      } else {
        filters.x_axis = xAxis;
        filters.group_by_hours = false;
      }

      retrieveChronologyGraph(selectedLayer, filters);
    }
  }, [chronologyFilters, selectedLayer, retrieveChronologyGraph, flowMap]);

  useEffect(() => {
    if (flowMap?.initialized) {
      const xAxis = categoryCountFilters?.x_axis || "HOUR";
      const filters = { x_axis: xAxis, group_by_hours: false };
      retrieveCategoryCountGraph(selectedLayer, filters);
    }
  }, [
    categoryCountFilters,
    selectedLayer,
    retrieveCategoryCountGraph,
    flowMap,
  ]);

  useEffect(() => {
    if (flowMap?.initialized) {
      const xAxis = trafficChartFilters?.x_axis || "HOUR";
      const filters = { x_axis: xAxis, group_by_hours: false };
      retrieveTrafficChart(selectedLayer, filters);
    }
  }, [trafficChartFilters, selectedLayer, retrieveTrafficChart, flowMap]);

  useEffect(() => {
    if (flowMap?.initialized) {
      retrieveTravelTimeGraph(selectedLayer);
    }
  }, [travelTimeFilters]);

  function updateDate(key, date) {
    return setDates((d) => ({ ...d, [key]: date.valueOf() }));
  }

  function handleDateChange(key, date) {
    if (key === "from") {
      updateDate("from", date.startOf("day"));

      if (date.isAfter(moment(dates.to), "day")) {
        updateDate("to", date.endOf("day"));
      }
    }

    if (key === "to") {
      updateDate("to", date.endOf("day"));

      if (date.isBefore(moment(dates.from), "day")) {
        updateDate("from", date.startOf("day"));
      }
    }
  }

  function search() {
    setFilter("from", parseInt(dates.from / 1000));
    setFilter("to", parseInt(dates.to / 1000));
    retrieveAllFlowStats();
  }

  return (
    <Container>
      <FlowFilters
        flowTypes={formattedFlowTypeFilters}
        setFilter={setFilter}
        startDate={{
          value: dates.from,
          label: intl.messages["flowScene.filters.date.start"],
          onChange: (date) => handleDateChange("from", date),
          maxDaysAllowed: ContractUtils.getFlowMaxDaysAllowed(contract),
        }}
        endDate={{
          value: dates.to,
          label: intl.messages["flowScene.filters.date.end"],
          onChange: (date) => handleDateChange("to", date),
        }}
        search={{
          label: intl.messages["components.buttons.search"],
          onClick: search,
        }}
        hideFlowTypes={selectedLayer?.apiType === "TRAFFIC"}
      />

      <FlowStatsSection isMultipleDays={isMultipleDays} />
    </Container>
  );
}

const mapStateToProps = (state) => ({
  contract: state.Contract.contract,
  currentFloor: state.Map?.config?.currentFloor,
  flowMap: state?.Flow?.map,
  filters: state?.Flow?.filters,
  selectedLayer: FlowUtils.getSelectedLayer(state.Flow),
  startDate: get(state.Flow, "filters.from") * 1000,
  endDate: get(state.Flow, "filters.to") * 1000,
  attendanceFilters: state.Flow?.filters?.attendance,
  travelTimeFilters: state.Flow?.filters?.travelTime,
  chronologyFilters: state.Flow?.filters?.chronology,
  categoryCountFilters: state.Flow?.filters?.categoryCount,
  trafficChartFilters: state.Flow?.filters?.trafficChart,
});

const mapDispatchToProps = (dispatch) => ({
  setFilter: (key, value) => dispatch(FlowActioner.setFilter(key, value)),
  retrieveAllFlowStats: () => dispatch(FlowActioner.retrieveAllStats()),
  retrieveAttendanceGraph: (layer, options) =>
    dispatch(FlowActioner.retrieveAttendanceGraph(layer, options)),
  retrieveTravelTimeGraph: (layer) =>
    dispatch(FlowActioner.retrieveTravelTimeGraph(layer)),
  setSelectedLayers: (layers) =>
    dispatch(FlowActioner.setSelectedLayers(layers)),
  retrieveChronologyGraph: (layer, options) =>
    dispatch(FlowActioner.retrieveChronologyGraph(layer, options)),
  retrieveCategoryCountGraph: (layer, options) =>
    dispatch(FlowActioner.retrieveCategoryCountGraph(layer, options)),
  retrieveTrafficChart: (layer, options) =>
    dispatch(FlowActioner.retrieveTrafficChart(layer, options)),
});

FlowLayout.defaultProps = {
  selectedLayer: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(FlowLayout);
